import { Smartphone } from 'lucide-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardLoadingIcon } from '@/components/Loaders';
import { OrganizationBlocked } from '@/components/OrganizationBlocked';
import { useUserState } from '@/state';

import { LogoutButton } from './StaffOnlyGate.styled';
import { resolveStaffOnlyGate, useStaffOnlyStore } from './staffOnlyStore';

/** Ekran zamiast panelu dla konta tylko do aplikacji Staff, z wylogowaniem. */
export const StaffOnlyScreen: FC = () => {
  const { t } = useTranslation();
  const { logout, currentUser } = useUserState();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async (): Promise<void> => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background">
      <OrganizationBlocked
        title={t('staffOnly.title', 'Brak dostępu do panelu')}
        message={t('staffOnly.message', 'To konto jest tylko do aplikacji Staff. Zaloguj się w aplikacji na telefonie.')}
        hint={currentUser?.email ?? undefined}
        icon={<Smartphone strokeWidth={1.5} />}
        action={(
          <LogoutButton onPress={handleLogout} isLoading={isLoggingOut} radius="full">
            {t('common.logout', 'Wyloguj')}
          </LogoutButton>
        )}
      />
    </div>
  );
};

interface StaffOnlyGateProps {
  children: ReactNode;
}

/**
 * Bramka przed panelem: konto z claimem `staffOnly` (także admin HotelSpot) albo takie,
 * któremu backend odpowiedział 403 `staff_only`, widzi ekran „tylko aplikacja Staff”.
 * Niezalogowanego przepuszcza dalej, bo przekierowanie na logowanie robią guardy niżej.
 */
export const StaffOnlyGate: FC<StaffOnlyGateProps> = ({ children }) => {
  const { currentUser } = useUserState();
  const blockedByApi = useStaffOnlyStore((state) => state.blocked);
  const setBlocked = useStaffOnlyStore((state) => state.setBlocked);
  const [claim, setClaim] = useState<{ uid: string; staffOnly: boolean } | null>(null);

  useEffect(() => {
    if (!currentUser) {
      // Po wylogowaniu następne konto zaczyna bez blokady z poprzedniej sesji.
      setBlocked(false);
      setClaim(null);
      return undefined;
    }
    let active = true;
    currentUser.getIdTokenResult()
      .then((result) => {
        if (active) setClaim({ uid: currentUser.uid, staffOnly: result.claims.staffOnly === true });
      })
      .catch(() => {
        // Brak tokenu rozstrzygnie backend (401/403), nie blokujemy panelu na zapas.
        if (active) setClaim({ uid: currentUser.uid, staffOnly: false });
      });
    return () => { active = false; };
  }, [currentUser, setBlocked]);

  const gate = resolveStaffOnlyGate({ currentUid: currentUser?.uid, claim, blockedByApi });
  if (gate === 'blocked') return <StaffOnlyScreen />;

  if (gate === 'loading') {
    return (
      <div className="w-full h-[100vh] flex flex-col items-center justify-center">
        <DashboardLoadingIcon size="sm" />
      </div>
    );
  }

  return children;
};
